import { useMemo } from 'react';
import { Property, Auction } from '../../types';
import { useAppStore } from '../../store/appStore';
import { OpenStreetMap } from '../common/OpenStreetMap';
import { REGIONS } from '../../data/mockData';

// Helper function to check if property has coordinates (explicit or from city/region)
function hasCoordinates(property: Property | Auction): boolean {
  if (property.location.lat && property.location.lng) {
    return true;
  }
  // Check if we can resolve coordinates from city/region
  const cityName = property.location.city;
  const regionName = property.location.region;
  
  for (const region of REGIONS) {
    if (region.name === regionName) {
      const city = region.cities.find(c => c.name === cityName);
      if (city?.lat && city?.lng) return true;
      if (region.lat && region.lng) return true;
    }
    const cityInAnyRegion = region.cities.find(c => c.name === cityName);
    if (cityInAnyRegion?.lat && cityInAnyRegion?.lng) return true;
  }
  return false;
}

interface InteractiveMapProps {
  properties?: (Property | Auction)[];
  onPropertySelect?: (property: Property | Auction) => void;
  onVisiblePropertiesChange?: (properties: (Property | Auction)[]) => void;
  height?: string;
  showFilters?: boolean;
  selectedRegion?: string;
  focusLocation?: { lat: number; lng: number; zoom?: number };
}

export function InteractiveMap({ 
  properties: propProperties, 
  height = '600px',
  selectedRegion,
  focusLocation,
}: InteractiveMapProps) {
  const { properties: storeProperties, auctions, demands } = useAppStore();
  
  const allProperties = useMemo(() => {
    return propProperties || [...storeProperties, ...auctions, ...demands];
  }, [propProperties, storeProperties, auctions, demands]);

  // Count properties with coordinates (explicit or resolved from city/region)
  const propertiesWithCoords = allProperties.filter(hasCoordinates);

  return (
    <div className="w-full h-full relative">
      <OpenStreetMap 
        properties={allProperties} 
        height={height}
        selectedRegion={selectedRegion}
        focusLocation={focusLocation}
      />
      
      {/* Info overlay */}
      <div className="absolute top-4 right-4 bg-white dark:bg-slate-800 p-2 rounded shadow-md z-[1000] text-xs">
        <p className="font-semibold text-gray-700 dark:text-gray-200">
          {propertiesWithCoords.length} lokacija na mapi
        </p>
        <p className="text-gray-500 dark:text-gray-400">Zumirajte za detaljniji prikaz</p>
      </div>
    </div>
  );
}
